/* Invoice History's export menu (Duncan 2026-10-15: "the same reporting and exporting that job
   cost has"): the PDF report or the Excel workbook, of what's showing (the filter bar's Export
   button) or of one job (the icon on its card). Looks and behaves like Job Costs' menu
   (components/jobcost/PsrExportMenu.js): "PDF report" opens the printable report in a new tab
   (pages/InvoiceReportPage.js) to print, save or email from there; "Excel workbook" builds the
   workbook here (lib/invoiceXlsx, loaded only when asked) and downloads it. */
import React, { useEffect, useRef, useState } from 'react';
import { JcIcon } from '../jobcost/JobCostShared';

const CSS = `
.ihx{position:relative;display:inline-flex}
.ihx-icon{width:30px;height:30px;border-radius:8px;display:inline-flex;align-items:center;justify-content:center;color:var(--text-3);background:var(--bg-surface);border:1px solid var(--border-default);box-shadow:var(--shadow-xs);cursor:pointer;transition:color .15s,border-color .15s,background .15s}
.ihx-btn{display:inline-flex;align-items:center;gap:6px;height:32px;padding:0 10px 0 13px;border-radius:8px;font-size:14px;font-weight:500;color:var(--text-2);background:var(--bg-surface);border:1px solid var(--border-default);box-shadow:var(--shadow-xs);cursor:pointer;transition:color .15s,border-color .15s,background .15s;font-family:var(--font-sans)}
.ihx-icon:hover,.ihx-icon[aria-expanded="true"],.ihx-btn:hover,.ihx-btn[aria-expanded="true"]{color:var(--accent-text);border-color:color-mix(in oklch, var(--accent) 35%, var(--border-default));background:var(--accent-soft)}
.ihx-menu{position:absolute;top:calc(100% + 6px);right:0;z-index:30;width:272px;padding:6px;border-radius:10px;background:var(--bg-surface);border:1px solid var(--border-default);box-shadow:var(--shadow-md);text-align:left}
.ihx-head{font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--text-4);padding:5px 8px 6px}
.ihx-item{display:flex;gap:10px;align-items:flex-start;width:100%;padding:8px;border:0;border-radius:7px;background:none;text-align:left;cursor:pointer;color:var(--text-1);font:inherit}
.ihx-item:hover,.ihx-item:focus-visible{background:var(--bg-surface-2);outline:none}
.ihx-item[disabled]{cursor:progress}
.ihx-item b{display:block;font-size:13.5px;font-weight:600;line-height:18px}
.ihx-item span{display:block;font-size:12px;line-height:16px;color:var(--text-3);margin-top:1px}
.ihx-err{font-size:12px;line-height:16px;color:var(--danger);padding:6px 8px 4px}
`;

const Glyph = ({ color, children }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none', marginTop: 1 }}>
    {children}
  </svg>
);
const PdfGlyph = () => (
  <Glyph color="var(--danger)"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /><path d="M9 13h6M9 17h4" /></Glyph>
);
const SheetGlyph = () => (
  <Glyph color="var(--success)"><rect x="4" y="4" width="16" height="16" rx="2" /><path d="M4 10h16M4 15h16M10 4v16" /></Glyph>
);

const InvoiceExportMenu = ({ heading, reportPath, onExcel, variant = 'button' }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const firstRef = useRef(null);

  // Open: focus the first choice, and close on a click anywhere else.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    const raf = requestAnimationFrame(() => { if (firstRef.current) firstRef.current.focus(); });
    return () => { document.removeEventListener('mousedown', onDown); cancelAnimationFrame(raf); };
  }, [open]);

  const close = () => {
    setOpen(false);
    setError('');
    requestAnimationFrame(() => { if (triggerRef.current) triggerRef.current.focus(); });
  };
  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'Escape') { e.stopPropagation(); close(); return; }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      const items = [...rootRef.current.querySelectorAll('.ihx-item')];
      const at = items.indexOf(document.activeElement);
      const next = items[(at + (e.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length];
      if (next) { e.preventDefault(); next.focus(); }
    }
  };
  const openPdf = () => {
    window.open(reportPath, '_blank', 'noopener');
    close();
  };
  const downloadExcel = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await onExcel();
      close();
    } catch (e) {
      setError(`The workbook couldn’t be built: ${e.message || 'unknown error'}`);
    } finally {
      setBusy(false);
    }
  };

  // A click or a key inside (on a job card) mustn't also open or close the card.
  return (
    <div className="ihx" ref={rootRef} onKeyDown={(e) => { onKeyDown(e); e.stopPropagation(); }} onClick={(e) => e.stopPropagation()} role="presentation">
      <style>{CSS}</style>
      {variant === 'icon' ? (
        <button type="button" ref={triggerRef} className="ihx-icon" onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu" aria-expanded={open} aria-label={heading} title="Export">
          <JcIcon name="download" size={15} sw={1.8} />
        </button>
      ) : (
        <button type="button" ref={triggerRef} className="ihx-btn" onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu" aria-expanded={open}>
          <JcIcon name="download" size={13} /> Export <JcIcon name="chevron-d" size={14} />
        </button>
      )}
      {open && (
        <div className="ihx-menu" role="menu" aria-label={heading}>
          <div className="ihx-head">{heading}</div>
          <button type="button" role="menuitem" className="ihx-item" ref={firstRef} onClick={openPdf}>
            <PdfGlyph />
            <div><b>PDF report</b><span>Opens the printable report to print, save or email</span></div>
          </button>
          <button type="button" role="menuitem" className="ihx-item" onClick={downloadExcel} disabled={busy} aria-busy={busy}>
            <SheetGlyph />
            <div><b>{busy ? 'Building the workbook…' : 'Excel workbook'}</b><span>Downloads the .xlsx to work with</span></div>
          </button>
          {error && <div className="ihx-err" role="alert">{error}</div>}
        </div>
      )}
    </div>
  );
};

export default InvoiceExportMenu;
